import React, { useCallback, useMemo, useState } from 'react';
import AvFilters from '@components/AvFilters';
import { NoDataFoundWidget } from '@components/Widgets/layout.components';
import { AvDateRangePicker, DatePickerStartAdornment, DateValue, DwQueryRequest, Filter } from '@fabric-ui/components';
import { History as HistoryData } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import useQueryObject from '@hooks/useQueryObjectSql';
import { Box, Typography } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { useParams } from '@utils/AvRouter';
import { useMeasurementsList } from '@views/Reports/hooks';
import { useAvContext } from '../../../context/AvContextProvider';
import AgentDetails from './AgentDetails';
import AgentStatus from './agentStatus';
import AgentTiles from './AgentTiles';
import AgentTimeSavedCard from './agentTimeSavedCard';
import MinResolution from './minResolution';
import { getAllAgents, getTotalAlertsCountByAgents } from './queries';
import {
  AgentDashboardLoadingWidget,
  agentRoiWidgetBoxStyle,
  DEFAULT_AGENT_DASHBOARD_DATE_FILTER,
  DEFAULT_AGENT_DASHBOARD_FILTER,
  FTEMultiplierFormula,
  TimeSavedFormula,
} from './utils';

const projName = 'agents';

const Agents = () => {
  const { id } = useParams();

  const [filters, setFilters] = useState<Filter>(DEFAULT_AGENT_DASHBOARD_FILTER);
  const [dateRange, setDateRange] = useState<Date[]>(DEFAULT_AGENT_DASHBOARD_DATE_FILTER);

  const onFilterChange = useCallback(newFilters => {
    setFilters(prevFilters => ({ ...prevFilters, ...newFilters }));
  }, []);
  const {
    accountEntities: { ingressProjs, aggProjs },
  } = useAvContext();
  const agentProj = ingressProjs[projName];
  const { data: measurements = [] } = useMeasurementsList({ entityName: agentProj?.entityTypeId?.name });

  const typeMap = useMemo(
    () => measurements.reduce((acc, { systemName, type }) => ({ ...acc, [systemName]: type }), agentProj?.typeMap),
    [measurements, agentProj]
  );

  const dims = useMemo(
    () => [...(ingressProjs.agents?.fieldList?.INTERACTIVE || []), ...(aggProjs.uber_alerts?.fieldList?.INTERACTIVE || [])],
    [ingressProjs, aggProjs]
  );

  const allAgentsQuery = useMemo(() => [getAllAgents as DwQueryRequest], []);

  const { data: totalAgents, isLoading: isTotalAgentsDataLoading } = useQueryObject({
    queryObject: allAgentsQuery,
    options: { placeholderData: keepPreviousData },
  });

  const filterContext = useMemo(() => ({ filters, dateRange, typeMap }), [filters, dateRange, typeMap]);

  const totalAlertsQueryObject = useMemo(() => [getTotalAlertsCountByAgents(filterContext) as DwQueryRequest], [filterContext]);

  const totalAlertCount = useQueryObject({
    queryObject: totalAlertsQueryObject,
    options: { placeholderData: keepPreviousData },
    onSuccess: data => data?.[0]?.cnt,
  });

  const onDateFilterChange = useCallback(timeRange => {
    const { from, to } = (timeRange?.value as DateValue) || {};
    setDateRange(from ? [from, to ?? new Date()] : []);
  }, []);

  const statuses = useMemo(
    () => ({
      Alerts_Investigated: <AgentStatus totalAlertCount={totalAlertCount} filterContext={filterContext} />,
      FTE_Multiplier: (
        <AgentTimeSavedCard
          title="FTE Multiplier"
          unit="Digital Headcount"
          percentageSub="Last 24 hours"
          percentageFormula={FTEMultiplierFormula}
          filterContext={filterContext}
        />
      ),
      Time_Saved: (
        <AgentTimeSavedCard
          title="Time Saved"
          unit="Hours"
          percentageSub="Hours Saved"
          infoMessage="Hours saved in manual work. Trend = last 24h vs. previous 24h."
          percentageFormula={TimeSavedFormula}
          filterContext={filterContext}
        />
      ),
      Sub_Min_Resolution: <MinResolution totalAlertCount={totalAlertCount} filterContext={filterContext} />,
    }),
    [filterContext, totalAlertCount]
  );

  return (
    <Box sx={{ ...flex.col, width: '100%', gap: 2 }}>
      <Box sx={{ ...flex.justifyBetween }}>
        <Typography variant="h3" sx={{ mb: 1, color: 'var(--color-content-base-primary)' }}>
          AI Workforce Command Center
        </Typography>

        <AvDateRangePicker
          showDropdownIcon
          inputSx={{ width: '500px' }}
          value={{ from: dateRange[0] }}
          icon={<DatePickerStartAdornment tooltipText="Historical Data" icon={<HistoryData />} />}
          onChange={onDateFilterChange}
          showRelativeOptions
          isRange
          allowFutureDates={false}
          showOptions={false}
        />
      </Box>
      {id ? (
        <AgentDetails filterContext={filterContext} />
      ) : isTotalAgentsDataLoading && !id ? (
        <AgentDashboardLoadingWidget />
      ) : totalAgents?.length === 0 ? (
        <NoDataFoundWidget />
      ) : (
        <>
          <Box sx={{ ...flex.justifyBetween }}>
            <AvFilters
              setFilters={setFilters}
              updateFilters={(key, value) => onFilterChange({ [key]: value })}
              filters={filters}
              onClearFilters={() => {
                setFilters(DEFAULT_AGENT_DASHBOARD_FILTER);
              }}
              dims={dims}
              isFilterExpression
              isGlobalFilter
              isDraggable
            />
          </Box>
          <Box sx={{ ...flex.col, gap: 2, overflowY: 'auto' }}>
            <Box sx={{ ...flex.row, gap: 2, width: '100%', overflow: 'auto', minHeight: 137 }}>
              {Object.entries(statuses).map(([key, component]) => (
                <Box key={key} sx={agentRoiWidgetBoxStyle}>
                  {component}
                </Box>
              ))}
            </Box>

            <AgentTiles filterContext={filterContext} />
          </Box>
        </>
      )}
    </Box>
  );
};

export default Agents;
